import React, { Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import { Blob } from './blob/Blob';
import CanvasCapture from './CanvasCapture';
import {
  isFaceScannerName,
  isProbioticArchitectureName,
  usesReducedBlobLighting,
} from './blob/resolveArtefactBlobConfig';
import { STUDIO_ENVIRONMENT_HDRI } from '../config/environmentHdri';

/** Fires once after Environment + Blob have mounted (inside Suspense) and one frame has rendered. */
function BlobReadyPulse({ onReady }) {
  const sentRef = useRef(false);
  useFrame(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    onReady?.();
  });
  return null;
}

function GlContextGuard({ onContextLost }) {
  const { gl } = useThree();
  useEffect(() => {
    if (!onContextLost) return undefined;
    const canvas = gl.domElement;
    const onLost = (event) => {
      event.preventDefault();
      onContextLost();
    };
    canvas.addEventListener('webglcontextlost', onLost);
    return () => canvas.removeEventListener('webglcontextlost', onLost);
  }, [gl, onContextLost]);
  return null;
}

/**
 * Landing artefact blob (WebGL). Optional onReady when the first frame has drawn.
 */
export default function LandingArtefactBlobCanvas({
  artefactName,
  cardTitle,
  gradient,
  config,
  icosahedronDetail,
  softenOverheadKey,
  onReady,
  onCaptureReady,
  onContextLost,
  className = '',
}) {
  const probioticLandingBlob = isProbioticArchitectureName(artefactName || cardTitle);
  const faceScannerLandingBlob = isFaceScannerName(artefactName || cardTitle);
  const reducedLightingBlob = usesReducedBlobLighting(artefactName || cardTitle);
  const renderedConfig = reducedLightingBlob
    ? { ...config, envMapIntensity: faceScannerLandingBlob ? 0.28 : 0.45, autoRotate: true, autoRotateSpeed: 1.8 }
    : { ...config, autoRotate: true, autoRotateSpeed: 1.8 };

  return (
    <Canvas
      className={className}
      camera={{ position: [0, 0, 4.5], fov: 54 }}
      dpr={probioticLandingBlob ? [1, 1.85] : [1, 1.35]}
      gl={{ antialias: true, alpha: true }}
    >
      {softenOverheadKey ? (
        <>
          <ambientLight intensity={faceScannerLandingBlob ? 0.22 : reducedLightingBlob ? 0.34 : 0.62} />
          <directionalLight
            position={[7, 0.35, 4]}
            intensity={faceScannerLandingBlob ? 0.28 : reducedLightingBlob ? 0.38 : 0.68}
          />
          <pointLight position={[-5, -5, 5]} intensity={faceScannerLandingBlob ? 0.12 : reducedLightingBlob ? 0.22 : 0.46} />
        </>
      ) : (
        <>
          <ambientLight intensity={faceScannerLandingBlob ? 0.22 : reducedLightingBlob ? 0.34 : 0.6} />
          <directionalLight
            position={[5, 5, 5]}
            intensity={faceScannerLandingBlob ? 0.28 : reducedLightingBlob ? 0.42 : 0.8}
          />
          <pointLight position={[-5, -5, 5]} intensity={faceScannerLandingBlob ? 0.12 : reducedLightingBlob ? 0.2 : 0.4} />
        </>
      )}
      <Suspense fallback={null}>
        <Environment
          files={STUDIO_ENVIRONMENT_HDRI}
          environmentIntensity={faceScannerLandingBlob ? 0.24 : reducedLightingBlob ? 0.38 : 0.82}
        />
        <group scale={[1.42, 1.42, 1.42]}>
          <Blob
            gradientColor1={gradient.c1}
            gradientColor2={gradient.c2}
            gradientColor3={gradient.c3}
            config={renderedConfig}
            icosahedronDetail={icosahedronDetail}
          />
        </group>
        {onContextLost ? <GlContextGuard onContextLost={onContextLost} /> : null}
        {onReady ? <BlobReadyPulse onReady={onReady} /> : null}
        {onCaptureReady ? <CanvasCapture onCaptureReady={onCaptureReady} /> : null}
      </Suspense>
    </Canvas>
  );
}
